import React, { FC, useCallback, useState } from 'react';

// @ts-ignore
import TitleSelect from '@r1-frontend/ui-react/components/select/titleSelect';
import { EPriceTypes } from '@r1-frontend/shared/types/EPriceTypes';

import { IClick } from '~/src/features/movix-new';
import { ELABELS } from '~/src/features/movix-new/constants';
import { EEquipmentType } from '~/src/features/movix-new/Popup';
import { dataLayerPushButton } from '~/src/features/movix-new/utils';
import { toCurrency } from '~/src/helpers/numbers';
import { ICityState } from '~/src/store/reducers/city';

import { MovixButton } from '~/src/features/movix-new/styled';
import * as ST from './styled';

export interface ICardItem {
    img: string,
    title: string | JSX.Element,
    descList?: JSX.Element[],
    description?: string | JSX.Element,
    price?: number,
    discountDefault?: string | JSX.Element,
    priceStartsFromText?: string,
    buttonText?: string,
    defaultAfterPrice?: number,
    fullPrice?: string,
    isBigImage?: boolean,
    gradientSettings?: IGradientSettings,
    id: number,
    costType?: EEquipmentType,
    notAllowButtonText?: boolean,
    notNeedBgStyleAdditional?: boolean,
}

export interface IGradientSettings {
    gradient: string,
    height: number,
    width?: number,
    top?: string,
    bottom?: string,
    right?: string,
    left?: string,
    borderRadius?: string,
}

export interface IPriceOption {
    label: string,
    operType: string,
    price: number,
    sort: number,
    type: number,
    value: number,
    pricePromo?: number,
}

interface IProps {
    item: ICardItem,
    city?: ICityState,
    priceOptions?: IPriceOption[],
    handleClick?: (data: IClick) => void,
}

const Card: FC<IProps> = ({
    item,
    priceOptions = [],
    handleClick,
}) => {
    const {
        title,
        price,
        img,
        description,
        descList,
        priceStartsFromText,
        buttonText,
        fullPrice,
        isBigImage,
        gradientSettings,
        notAllowButtonText,
        notNeedBgStyleAdditional,
    } = item;
    const [selectedOption, setSelectedOption] = useState(priceOptions[priceOptions.length - 1]);
    const [isSelectOpen, setIsSelectOpen] = useState(false);

    const getCurrency = useCallback(() => {
        if (!selectedOption) {
            return;
        }
        return selectedOption.type === EPriceTypes.Possess
            ? ' ₽'
            : ' ₽/мес';
    }, [selectedOption]);

    const changeOptionsHandler = useCallback(option => {
        setSelectedOption(option);
        setIsSelectOpen(false);
    }, []);

    const toggleSelectHandler = useCallback(() => {
        setIsSelectOpen(!isSelectOpen);
    }, [isSelectOpen]);

    const getPriceLabel = useCallback(() => {
        if (!selectedOption) {
            return;
        }
        return selectedOption.type === EPriceTypes.Possess
            ? 'Покупка'
            : `рассрочка на ${selectedOption.value} мес.`;
    }, [selectedOption]);

    const handleClickDataLayerWrapper = useCallback((data: IClick) => {
        handleClick && handleClick(data);
        dataLayerPushButton(data.label);
    }, []);

    const handleClickWrapper = () => {
        let costType;
        let label;
        const leasingDurationForTariff = 36;
        if (price) {
            costType = EEquipmentType.leasing;
            label = ELABELS.tariff;
        } else if (selectedOption) {
            if (selectedOption.type === EPriceTypes.Possess) {
                costType = EEquipmentType.sale;
            } else {
                costType = EEquipmentType.leasing;
            }
            label = ELABELS.buy;
        } else {
            costType = EEquipmentType.loan;
            label = ELABELS.testDrive;
        }
        const data = {
            costType,
            leasingDuration: price ? leasingDurationForTariff : selectedOption?.value,
            textButton: notAllowButtonText ? '' : buttonText,
            label,
        };

        handleClick && handleClickDataLayerWrapper(data);
    };

    const assertPriceMode = () => {
        if (priceStartsFromText) {
            return priceStartsFromText + price;
        }
        return price;
    };

    return (
        <>
            <ST.CardWrapper>
                <ST.Image
                    path={img}
                    isBigImage={isBigImage}
                    gradientSettings={gradientSettings}
                    notNeedBgStyleAdditional={notNeedBgStyleAdditional}
                />
                <ST.Title>{title}</ST.Title>
                {description && <ST.Description>{description}</ST.Description>}
                {descList && (
                    <ST.DescriptionList>
                        {descList.map((text, index) => <li key={index}>{text}</li>)}
                    </ST.DescriptionList>
                )}
                {price && (
                    <ST.PriceBlock>
                        <ST.Price>{assertPriceMode()} ₽/мес</ST.Price>
                    </ST.PriceBlock>
                )}
                {fullPrice && (
                    <ST.FullPriceBlock>
                        {priceOptions && (
                            <>
                                <ST.Leasing onClick={toggleSelectHandler} isActive={isSelectOpen}>{getPriceLabel()}</ST.Leasing>
                                {isSelectOpen && <TitleSelect
                                    options={priceOptions}
                                    minWidth='220px'
                                    value={selectedOption}
                                    onChange={changeOptionsHandler}
                                    autoFocus
                                    backspaceRemovesValue={false}
                                    controlShouldRenderValue={false}
                                    hideSelectedOptions={false}
                                    isClearable={false}
                                    menuIsOpen
                                    tabSelectsValue={false}
                                    extStyle={{
                                        indicatorsContainer: (provided: { [key: string]: string }) => ({
                                            ...provided,
                                            display: 'none',
                                        }),
                                        container: (provided: { [key: string]: string }) => ({
                                            ...provided,
                                            position: 'absolute',
                                        }),

                                        menu: (provided: { [key: string]: string }) => ({
                                            ...provided,
                                            minWidth: '220px',
                                            overflow: 'hidden',
                                            zIndex: 2,
                                        }),
                                    }}
                                />}
                            </>

                        )}
                        <ST.FullPrice>{toCurrency(selectedOption?.price)}{getCurrency()}</ST.FullPrice>
                    </ST.FullPriceBlock>
                )}
                {buttonText &&
                    <ST.ButtonBlock>
                        <MovixButton
                            bType={'secondary'}
                            onClick={handleClickWrapper}
                        >
                            {buttonText}
                        </MovixButton>
                    </ST.ButtonBlock>}
            </ST.CardWrapper>
        </>
    );
};

export default Card;
